/**
 * ทีมของฉัน (ใต้บังคับบัญชา) — หัวหน้างานเห็นรายชื่อลูกทีมที่ตั้ง "อีเมลหัวหน้างาน"
 * เป็นอีเมลของตน. การตั้งหัวหน้าทำที่หน้าแก้ไขพนักงาน (ช่อง supervisor_email).
 */
import { AdminLayout } from '../components/layout/AdminLayout';
import { Loading } from '../components/layout/Loading';
import { useMySubordinates } from '../hooks';
import { Users, Phone } from 'lucide-react';

const STATUS_LABEL: Record<string, string> = { active: 'ทำงาน', probation: 'ทดลองงาน' };

export function MyTeam() {
  const { data, isLoading } = useMySubordinates();

  if (isLoading) {
    return <AdminLayout title="ทีมของฉัน"><Loading /></AdminLayout>;
  }

  const subs = data?.subordinates ?? [];
  // จัดกลุ่มตามแผนก
  const byDept = new Map<string, typeof subs>();
  for (const s of subs) {
    const k = s.department || 'ไม่ระบุแผนก';
    if (!byDept.has(k)) byDept.set(k, []);
    byDept.get(k)!.push(s);
  }

  return (
    <AdminLayout title="ทีมของฉัน" subtitle="พนักงานที่อยู่ใต้บังคับบัญชาของคุณ">
      {subs.length === 0 ? (
        <div className="card p-6 text-center">
          <Users className="w-12 h-12 mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-600">ยังไม่มีใครถูกตั้งให้คุณเป็นหัวหน้างาน</p>
          <p className="text-xs text-gray-400 mt-1">HR ตั้งได้ที่ข้อมูลพนักงาน → “หัวหน้างาน” = ชื่อคุณ</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="card p-3 flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-teal-600" />
            <span className="font-medium">{subs.length} คน</span>
            <span className="text-gray-400">ในทีมของคุณ</span>
          </div>
          {[...byDept.entries()].map(([dept, list]) => (
            <div key={dept} className="card overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500">{dept} · {list.length} คน</div>
              <div className="divide-y divide-gray-100">
                {list.map((s) => (
                  <div key={s.emp_code} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="w-9 h-9 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center text-sm font-semibold shrink-0">
                      {s.name.slice(0, 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{s.name}{s.nickname ? ` (${s.nickname})` : ''}</div>
                      <div className="text-xs text-gray-500 truncate">{s.position ?? '—'}</div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'probation' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                      {STATUS_LABEL[s.status] ?? s.status}
                    </span>
                    {s.phone && (
                      <a href={`tel:${s.phone}`} className="text-gray-400 hover:text-teal-600"><Phone className="w-4 h-4" /></a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
